import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { Button, Flex, Input, Pagination, Space, Table } from 'antd';
import type { SearchProps } from 'antd/es/input/Search';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../Layout';
import BreadcrumbBlock from '../../components/BreadcrumbBlock/BreadcrumbBlock';
import ColSetting from '../../components/Filter/ColSetting';
import ModalDeleteManager from '../../components/ModalDeleteManager';
import useToast from '../../hook/useToast';
import { formatNumber, mixGetV2, mixPostV2 } from '../../services/mixin';
import { selectorInfoUser } from '../../store/slices/infoUserSlice';
import ImportDataModal from './Component/ImportDataModal/ImportDataModalV1';
import './CustomerList.scss';

const nameTableCache = 'TbCachePoolingDateCustomerList';

const getDefaultPagination = () => ({ current: 1, pageSize: 10, total: 0 });

const CustomerList: React.FC = () => {
  const { id } = useSelector(selectorInfoUser);
  const pushToast = useToast();
  const navigate = useNavigate()
  // TABLE COLUMNS
  const columns = [
    {
      title: 'Hành động',
      dataIndex: '',
      key: 'action',
      render: (_d: any, row: any) => (
        <Flex align='center' className='gap-10px'>
          <Button className="rs-button rs-button--icon-only" onClick={() => {
            navigate(`/customer/pooling-data/customer-list/${row.warehouse_id_origin}?id_sync=${row.id}`);
          }}><EyeOutlined /></Button>
        </Flex>
      ),
    },
    {
      title: 'Mã định danh khách hàng',
      dataIndex: 'cus_id',
      key: 'cus_id',
    },
    {
      title: 'Họ tên',
      dataIndex: 'cus_name',
      key: 'cus_name',
    },
    {
      title: 'CCCD/CMND',
      dataIndex: 'cus_dms_id_card_no',
      key: 'cus_dms_id_card_no',
    },
    {
      title: 'Mã số thuế',
      dataIndex: 'cus_tax_reg_no_dms',
      key: 'cus_tax_reg_no_dms',
    },
    {
      title: 'Ngày sinh',
      dataIndex: 'cus_birth_day',
      key: 'cus_birth_day',
    },
    {
      title: 'Giới tính',
      dataIndex: 'cus_gender_name',
      key: 'cus_gender_name',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'cus_tel',
      key: 'cus_tel',
    },
    {
      title: 'Email',
      dataIndex: 'cus_email',
      key: 'cus_email',
    },
  ];

  // State
  const [listMigrations, setListMigrations] = useState([
    { label: 'Email + CCCD/CMND + Phone', value: '1' },
  ] as any)
  const [isSettingShow, setSettingShow] = useState(false);
  const [openDeleteListCustomer, setOpenDeleteListCustomer] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [idCustomer, setIdCustomer] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [megaTableData, setMegaTableData] = useState<any>({
    col: [],
    listData: [],
    open: false,
    loading: true,
  });
  const [tempColData, setTempColData] = useState<any[]>([]);
  const [pagination, setPagination] = useState(getDefaultPagination());
  const [paramListData, setParamListData] = useState<any>({
    'sort[]': 'id,desc',
    page: 1,
    limit: 10,
    keyword: '',
    source_id: null,
    list_key: '[]',
  });
  const [listData, setListData] = useState<any[]>([]);
  const [showImportDataModal, setShowImportDataModal] = useState(false);
  const [paramKeys, setParamKeys] = useState<any[]>([]);
  const [paramMap, setParamMap] = useState<any[]>([]);
  const [source, setSource] = useState<any[]>([]);

  // Use ref to avoid re-creating debounce/save-cache
  const saveCacheRef = useRef<ReturnType<typeof getSaveCache>>();

  // Debounce saveCache to avoid frequent calls
  function getSaveCache() {
    let timeout: NodeJS.Timeout | null = null;
    let lastArgs: any = null;

    return async (data: any, name: string) => {
      if (Object.keys(data.listData).length === 0) return;
      lastArgs = { data, name };
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(async () => {
        const { data, name } = lastArgs;
        const param = {
          user_id_thaco: id,
          name: nameTableCache,
          data_json: JSON.stringify(data),
        };
        await mixPostV2('/api/v1/be/user_thaco_json', param);
      }, 800);
    }
  }
  // Init saveCache on first render
  useEffect(() => {
    saveCacheRef.current = getSaveCache();
  }, [id]);

  // Search
  const { Search } = Input;
  const onSearch: SearchProps['onSearch'] = (value) => {
    setParamListData((prev: any) => ({
      ...prev,
      keyword: value,
      page: 1,
      limit: pagination.pageSize,
      data_migration: '1'
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // Pagination
  const handleChangePagination = (current: number, pageSize: number) => {
    setPagination({ ...pagination, current, pageSize });
    setParamListData((prev: any) => ({ ...prev, limit: pageSize, page: current }));
  };

  // Fetch List Data
  useEffect(() => {
    let ignore = false;
    setLoading(true);
    mixGetV2('/api/v1/be/warehouses_elastic', paramListData)
      .then(res => {
        if (!ignore) {
          setLoading(false);
          if (res.status) {
            const temp = [] as any;
            res.data.items.forEach((val: any) => {
              temp.push({
                ...val,
                key: val.id,
              })
            });

            setListData(temp);
            setPagination((prev) => ({ ...prev, total: res.data.total }));
          }
        }
      });
    return () => { ignore = true; }
  }, [paramListData]);

  // Delete modal
  const openModalDelete = (id: number) => {
    setIdCustomer(id);
    setOpenDeleteListCustomer(true);
  };
  const closeModalDelete = () => {
    setIdCustomer(undefined);
    setOpenDeleteListCustomer(false);
  };
  const onConfirmDelete = async () => {
    const param = {
      list_ids: idCustomer?.toString()
    };
    // const res = await mixPostV2('/api/v1/be/warehouse_delete', param);
    const res = await mixGetV2('/api/v1/be/elastic/create_index');
    if (res.status) {
      pushToast('Xóa thành công', "success");
      closeModalDelete();
      setSelectedRowKeys([]);
      // Reload list
      setParamListData((prev: any) => ({ ...prev }));
    } else {
      pushToast('Error', "warning");
    }
  };

  // Multi Delete
  const mutiDel = (ids: React.Key[]) => {
    setIdCustomer(ids as any); // type can be array for multi delete
    setOpenDeleteListCustomer(true);
  };

  const [showSelectFilter, setActive] = useState(false);
  const [isShowSelect, setShowSelect] = useState('Email + CCCD/CMND + Phone');
  const handleClearFilter = (isShow: any) => {
    setActive(false)
    setShowSelect(isShow)
  }

  // Column Data
  useEffect(() => {
    let ignore = false;
    async function getcolumn() {
      const [res, res2] = await Promise.all([
        mixGetV2('/api/v1/be/source_column_warehouses?is_active=1'),
        mixPostV2(`/api/v1/be/user_thaco_json_more`, {
          user_id_thaco: id,
          name: JSON.stringify([nameTableCache]),
        }),
      ]);
      if (!ignore && res.status) {
        matchCols(res.data.items, res2.status && res2.data.items[0] ? JSON.parse(res2.data.items[0].data_json) : {});
      }
    }
    getcolumn();
    return () => { ignore = true; }
    // eslint-disable-next-line
  }, [id]);

  // Match Columns
  const matchCols = useCallback((cols = [], dataJson: any) => {
    const tbCache = dataJson;
    const ob = tbCache.listData || {};

    const temp = [
      {
        headerName: "Hành động",
        field: "hanhdong",
        colId: "hanhdong",
        width: ob['hanhdong'] ? ob['hanhdong'].width : 100,
        pos: ob['hanhdong'] ? ob['hanhdong'].pos : 0,
        hide: ob['hanhdong'] ? ob['hanhdong'].hide : false,
        cellRenderer: (params: any) => (
          <Space size="middle">
            <DeleteOutlined
              className='text-F5222D text-sm border-EAECF0 p-5-px bg-white rounded-2'
              onClick={() => openModalDelete(Number(params.data.warehouse_id_origin))}
            />
            <EyeOutlined
              className='text-sm border-EAECF0 p-5-px bg-white rounded-2'
              onClick={() => {
                navigate(`/customer/pooling-data/customer-list/${params.data.id}`);
              }}
            />
          </Space>
        )
      },
      {
        headerName: "ID",
        field: "warehouse_id_origin",
        colId: "warehouse_id_origin",
        width: ob['warehouse_id_origin'] ? ob['warehouse_id_origin'].width : 100,
        pos: ob['warehouse_id_origin'] ? ob['warehouse_id_origin'].pos : 0,
        hide: ob['warehouse_id_origin'] ? ob['warehouse_id_origin'].hide : false,
      },
      {
        headerName: "Ngày cập nhật",
        field: "updated_at",
        colId: "updated_at",
        width: ob['updated_at'] ? ob['updated_at'].width : 200,
        pos: ob['updated_at'] ? ob['updated_at'].pos : 0,
        hide: ob['updated_at'] ? ob['updated_at'].hide : false,
      },
    ];

    const firstView = Object.keys(ob).length === 0;
    const notShowCols = temp.map((val: any) => val.field);

    cols.forEach((val: any, index: any) => {
      const item = {
        headerName: val.name,
        field: val.column_warehouse.column,
        colId: val.id,
        width: ob[val.id] ? ob[val.id].width : 100,
        pos: ob[val.id] ? ob[val.id].pos : index,
        hide: firstView && val.is_default === 1 ? false : ob[val.id] ? ob[val.id].hide : true,
        cellRenderer: (params: any) => (
          <div className='table-wrap-text'>{params.value}</div>
        )
      };
      temp.push(item);
    });

    temp.sort((a: any, b: any) => a.pos - b.pos);

    setMegaTableData((prev: any) => ({ ...prev, col: temp, screen: tbCache.screen || window.innerWidth }));
    setTempColData([...temp]);
    if (firstView) {
      setParamListData((prev: any) => ({
        ...prev,
        list_key: JSON.stringify(
          temp.filter((col: any) => col.hide === false && !notShowCols.includes(col.field)).map((col: any) => col.field)
        ),
      }));
    } else {
      setParamListData((prev: any) => ({
        ...prev,
        list_key: JSON.stringify(
          cols.filter(
            (col: any) => !notShowCols.includes(col.column_warehouse.column)
          ).map((col: any) => col.column_warehouse.column)
        ),
      }));
    }
  }, []);

  // Get ParamKeys, ParamMap, Source
  useEffect(() => {
    mixGetV2('/api/v1/be/source_column_warehouses?is_active=1').then(res => {
      if (res.status) {
        const temp = [] as any
        res.data.items.map((val: any) => {
          const listSourceIds = [] as any
          val.params_key.map((val: any) => {
            listSourceIds.push(val.source_id)
            return true
          })
          temp.push({ id: val.id, name: val.name, column: val.column_warehouse.column, source_id: listSourceIds, type: val.type_id })
          return true
        })
        setParamKeys(temp);
      }
    });
  }, []);
  useEffect(() => { mixGetV2('/api/v1/be/setting_mapping?type=2').then(res => res.status && setParamMap(JSON.parse(res.data.value || '[]'))); }, []);
  useEffect(() => { mixGetV2('/api/v1/be/sources').then(res => res.status && setSource(res.data.items)); }, []);

  // MegaTable onChange
  const handleMegaTableChange = useCallback((data: any, type: string) => {
    setTempColData([...data]);
    if (type === 'colChange') {
      const temp: any = {};
      data.forEach((val: any, index: any) => {
        temp[val.colId] = {
          colId: val.colId,
          field: val.field,
          headerName: val.headerName,
          hide: val.hide || false,
          width: val.width,
          pos: index,
        };
      });
      const newCache = { listData: temp, screen: window.innerWidth };
      saveCacheRef.current && saveCacheRef.current(newCache, nameTableCache);
    }
    if (type === 'onSelectedRows') {
      setSelectedRowKeys(data);
    }
  }, []);

  // ColSetting onChange
  const handleColSettingChange = useCallback((data: any, type: string) => {
    if (type === 'close') {
      setMegaTableData((prev: any) => ({ ...prev, open: false }));
    }
    if (type === 'saveChange') {
      setMegaTableData((prev: any) => ({ ...prev, col: data, open: false }));
      setTempColData([...data]);
      let temp: any = {};
      const listFields: any[] = [];
      data.forEach((val: any, index: any) => {
        temp[val.colId] = {
          ...val,
          pos: index,
        };
        if (!val.hide && val.field !== 'hanhdong' && val.field !== 'warehouse_id_origin') {
          listFields.push(val.field);
        }
      });
      const newCache = { listData: temp, screen: window.innerWidth };
      saveCacheRef.current && saveCacheRef.current(newCache, nameTableCache);
      if (listFields.length > 0) {
        setParamListData((prev: any) => ({
          ...prev,
          list_key: JSON.stringify(listFields),
        }));
      }
    }
  }, []);

  // Render logic
  const hasSelected = selectedRowKeys.length > 0;

  return (
    <>
      <Layout className='customer-list'>
        <BreadcrumbBlock
          items={[
            { title: <Link to="/">THACO AUTO CRM</Link> },
            { title: 'Khách hàng' },
            { title: <Link to="/customer/pooling-data">Gộp dữ liệu khách hàng</Link> },
            { title: 'Lịch sử danh sách khách hàng được đưa vào CRM' },
          ]}
          title='Lịch sử danh sách khách hàng được đưa vào CRM'
          backLink='/customer/pooling-data'
          className='gutter-block-end'
        />

        <div className='filter-block filter-label-left t-mb-18 head-box-bw'>
          <div className='filter-item' style={{ width: '500px' }}>
            <div className="title-dk">Bộ điều kiện</div>
            <div className='filter-select'>
              <div
                className='btn-select-show'
              >
                <span className="content-select" onClick={() => setActive(true)} >{isShowSelect}</span>
                <div
                  className={`icon-down ${showSelectFilter ? "active" : ""}`}
                  onClick={() => setActive(true)}
                >
                  <DownOutlined />
                </div>
                <div
                  className={`icon-close ${showSelectFilter ? "active" : ""}`}
                  onClick={() => { handleClearFilter('Email + CCCD/CMND + Phone'); setParamListData({ ...paramListData, data_migration: null }) }}
                >
                  <CloseCircleOutlined />
                </div>
              </div>
              <div className={`list-filter ${showSelectFilter ? "active" : ""}`}>
                <div className="list-item">
                  {listMigrations.map((val: any, index: any) => (
                    <div key={index} className="item-select" onClick={() => {
                      handleClearFilter(val.label);
                      setParamListData({ ...paramListData, data_migration: val.value })
                      sessionStorage.setItem('paramListDatax', val.value);
                    }}
                    >
                      {val.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className='search'>
            <div className='t-search-block'>
              <Search placeholder="Tìm kiếm" onSearch={onSearch} />
            </div>
          </div>
        </div>

        {/* Filter block for selected */}
        <div className={`filter-block filter-active-show ${hasSelected ? "active" : ""}`}>
          <div className='total-chosse'>
            {hasSelected ? `Đã chọn ${selectedRowKeys.length} khách hàng` : ''}
          </div>
          <div className="btn-input" onClick={() => { mutiDel(selectedRowKeys) }}>
            <div className="icon-svg"><DeleteOutlined /></div>
            <div className="text">{`Xóa ${selectedRowKeys.length} khách hàng`}</div>
          </div>
        </div>

        <div className='megatable'>
          <Table
            columns={columns}
            dataSource={listData}
            showSorterTooltip={false}
            rowKey="id"
            className="rs-table"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </div>

        <div className="t-table-block t-table-customer c-table-scoll-x">
          <div className="pagination-block">
            <Pagination
              className='rs-pagination'
              showSizeChanger
              total={pagination.total}
              defaultPageSize={pagination.pageSize}
              current={pagination.current}
               itemRender={(page, type, originalElement) => {
                                    // Chỉ custom khi là nút số trang
                                    if (type === 'page') {
                                        return <span>{formatNumber(page)}</span>;
                                    }
                                    // prev / next / jump-prev / jump-next giữ nguyên
                                    return originalElement;
                                }}
              showTotal={() => `Tổng cộng ${Math.ceil(pagination.total / pagination.pageSize) === 0 ? 1 : Math.ceil(pagination.total / pagination.pageSize)} trang`}
              locale={{ items_per_page: 'trang' }}
              onChange={handleChangePagination}
            />
          </div>
        </div>

        <ColSetting
          stData={{ ...megaTableData }}
          onChange={handleColSettingChange}
        />
      </Layout>

      <ModalDeleteManager
        isOpen={openDeleteListCustomer}
        title="Bạn có chắc chắn xóa khách hàng này"
        titleDelete="Xóa khách hàng"
        content="Khách hàng và dữ liệu liên quan sẽ bị xóa vĩnh viễn không thể khôi phục"
        closeModal={closeModalDelete}
        onConfirm={onConfirmDelete}
      />

      {/* MODAL IMPORT DATA */}
      <ImportDataModal
        show={showImportDataModal}
        columns={paramKeys}
        source={source}
        paramMap={paramMap}
        paramKeysChange={() => {
          mixGetV2('/api/v1/be/source_column_warehouses?is_active=1').then(res => {
            if (res.status) {
              const temp = [] as any
              res.data.items.map((val: any) => {
                const listSourceIds = [] as any
                val.params_key.map((val: any) => {
                  listSourceIds.push(val.source_id)
                  return true
                })
                temp.push({ id: val.id, name: val.name, column: val.column_warehouse.column, source_id: listSourceIds, type: val.type_id })
                return true
              })
              setParamKeys(temp);
            }
          });
        }}
        paramMapChange={() => mixGetV2('/api/v1/be/setting_mapping?type=2').then(res => res.status && setParamMap(JSON.parse(res.data.value || '[]')))}
        modalClose={() => {
          setShowImportDataModal(false);
          setParamListData((prev: any) => ({ ...prev }));
        }}
      />
    </>
  );
};

export default CustomerList;
